import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button, Form, InputGroup } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import { baseurladdress } from "../baseurl";

const TYPE_OPTIONS = [
  { value: "0", label: "Core Doctors", note: "Move secondary and unassigned doctors into core" },
  { value: "1", label: "Secondary Doctors", note: "Move core and unassigned doctors into secondary" },
];

const PAGE_SIZE = 8;

function formatTypeLabel(value) {
  if (value === null || value === undefined || value === "") return "None assigned";
  return Number(value) === 0 ? "Core" : "Secondary";
}

function getIndicatorMeta(value, zeroLabel, nonZeroLabel) {
  return Number(value) === 0
    ? { label: zeroLabel, dotClass: "bg-success" }
    : { label: nonZeroLabel, dotClass: "bg-danger" };
}

function DoctorBulkAssign() {
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("id");

  const api = useMemo(
    () =>
      axios.create({
        baseURL: baseurladdress,
        headers: { Authorization: `Bearer ${token}` },
      }),
    [token]
  );

  const [targetType, setTargetType] = useState("0");
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [viewMode, setViewMode] = useState("doctor");
  const [staffOptions, setStaffOptions] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState("");
  const [staffDoctors, setStaffDoctors] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [updateIntroBy, setUpdateIntroBy] = useState(false);
  const [doctorPage, setDoctorPage] = useState(1);
  const [staffPage, setStaffPage] = useState(1);

  const loadDoctors = async () => {
    setLoading(true);
    try {
      const res = await api.get("doctors/doctorlist");
      setDoctors(Array.isArray(res.data?.doctors) ? res.data.doctors : []);
    } catch (err) {
      console.error("doctorlist error:", err);
      Swal.fire("Error", "Failed to load doctor list.", "error");
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStaff = async () => {
    try {
      const res = await api.get("staff/findallstaff");
      const staff = Array.isArray(res.data?.staff) ? res.data.staff : [];
      const nextSelectedStaff = (() => {
        const current = selectedStaff;
        if (current && staff.some((item) => String(item.staff_id) === String(current))) {
          return current;
        }
        return staff[0]?.staff_id ? String(staff[0].staff_id) : "";
      })();
      setStaffOptions(staff);
      setSelectedStaff(nextSelectedStaff);
      return nextSelectedStaff;
    } catch (err) {
      console.error("findallstaff error:", err);
      Swal.fire("Error", "Failed to load staff list.", "error");
      setStaffOptions([]);
      setSelectedStaff("");
      return "";
    }
  };

  const loadStaffDoctors = async (staffId) => {
    if (!staffId) {
      setStaffDoctors([]);
      setSelectedIds(new Set());
      return;
    }

    setStaffLoading(true);
    try {
      let res;
      try {
        res = await api.get("doctors/doctorstaffdata", {
          params: { staff_id: staffId },
        });
      } catch (firstErr) {
        res = await api.get(`doctors/doctorstaffdata/${staffId}`);
      }

      setStaffDoctors(Array.isArray(res.data?.doctors) ? res.data.doctors : []);
      setSelectedIds(new Set());
    } catch (err) {
      console.error("doctorstaffdata error:", err);
      Swal.fire("Error", "Failed to load doctors for the selected staff.", "error");
      setStaffDoctors([]);
      setSelectedIds(new Set());
    } finally {
      setStaffLoading(false);
    }
  };

  useEffect(() => {
    loadDoctors();
    loadStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (viewMode !== "staff") return;
    loadStaffDoctors(selectedStaff);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStaff, viewMode]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [targetType]);

  useEffect(() => {
    setSelectedIds(new Set());
    setSearch("");
  }, [viewMode]);

  useEffect(() => {
    if (viewMode === "doctor") {
      setDoctorPage(1);
    } else {
      setStaffPage(1);
    }
  }, [search, viewMode]);

  const eligibleDoctors = useMemo(() => {
    const query = search.trim().toLowerCase();
    return doctors.filter((doctor) => {
      const currentType =
        doctor.doctor_type === null || doctor.doctor_type === undefined || doctor.doctor_type === ""
          ? null
          : Number(doctor.doctor_type);
      const matchesTarget = Number(targetType) === 0 ? currentType !== 0 : currentType !== 1;
      const matchesSearch =
        !query ||
        doctor.doctor_name?.toLowerCase().includes(query) ||
        formatTypeLabel(currentType).toLowerCase().includes(query);
      return matchesTarget && matchesSearch;
    });
  }, [doctors, search, targetType]);

  const visibleStaffDoctors = useMemo(() => {
    const query = search.trim().toLowerCase();
    return staffDoctors.filter((doctor) => {
      if (!query) return true;
      return (
        doctor.doctor_name?.toLowerCase().includes(query) ||
        formatTypeLabel(doctor.doctor_type).toLowerCase().includes(query) ||
        String(doctor.doctor_introby ?? "").toLowerCase().includes(query)
      );
    });
  }, [search, staffDoctors]);

  const doctorTotalPages = Math.max(1, Math.ceil(eligibleDoctors.length / PAGE_SIZE));
  const staffTotalPages = Math.max(1, Math.ceil(visibleStaffDoctors.length / PAGE_SIZE));
  const currentDoctorPage = Math.min(doctorPage, doctorTotalPages);
  const currentStaffPage = Math.min(staffPage, staffTotalPages);

  const pagedDoctors = useMemo(() => {
    const start = (currentDoctorPage - 1) * PAGE_SIZE;
    return eligibleDoctors.slice(start, start + PAGE_SIZE);
  }, [currentDoctorPage, eligibleDoctors]);

  const pagedStaffDoctors = useMemo(() => {
    const start = (currentStaffPage - 1) * PAGE_SIZE;
    return visibleStaffDoctors.slice(start, start + PAGE_SIZE);
  }, [currentStaffPage, visibleStaffDoctors]);

  const toggleSelected = (doctorId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(doctorId)) next.delete(doctorId);
      else next.add(doctorId);
      return next;
    });
  };

  const selectAllVisible = () => {
    const source = viewMode === "staff" ? pagedStaffDoctors : pagedDoctors;
    setSelectedIds(new Set(source.map((doctor) => doctor.doctor_id)));
  };

  const goToDoctorPage = (page) => {
    setDoctorPage(Math.max(1, Math.min(page, doctorTotalPages)));
  };

  const goToStaffPage = (page) => {
    setStaffPage(Math.max(1, Math.min(page, staffTotalPages)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const currentOption = TYPE_OPTIONS.find((option) => option.value === targetType) || TYPE_OPTIONS[0];
  const currentStaff = useMemo(
    () => staffOptions.find((item) => String(item.staff_id) === String(selectedStaff)) || null,
    [selectedStaff, staffOptions]
  );

  const refreshCurrentView = async () => {
    if (viewMode === "staff") {
      const nextSelectedStaff = await loadStaff();
      if (nextSelectedStaff) {
        await loadStaffDoctors(nextSelectedStaff);
      }
      return;
    }
    await loadDoctors();
  };

  const handleSubmit = async () => {
    if (selectedIds.size === 0) {
      Swal.fire("Select doctors", "Choose at least one doctor before saving.", "info");
      return;
    }

    if (viewMode === "staff" && !selectedStaff) {
      Swal.fire("Select staff", "Choose a staff member before saving.", "info");
      return;
    }

    const confirm = await Swal.fire({
      title: viewMode === "staff" ? "Assign doctors to staff?" : "Update doctor type?",
      text:
        viewMode === "staff"
          ? `This will assign ${selectedIds.size} doctor(s) to ${currentStaff?.staff_name || "the selected staff"}${
              updateIntroBy ? " and update intro by too" : ""
            }.`
          : `This will move ${selectedIds.size} doctor(s) to ${Number(targetType) === 0 ? "Core" : "Secondary"}.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
    });

    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      if (viewMode === "staff") {
        const selectedDoctors = staffDoctors.filter((doctor) => selectedIds.has(doctor.doctor_id));
        const results = [];

        for (const doctor of selectedDoctors) {
          try {
            const payload = {
              doctor_id: doctor.doctor_id,
              doctor_servedby: selectedStaff,
              doctor_updby: userId,
            };

            if (updateIntroBy) {
              payload.doctor_introby = selectedStaff;
            }

            const res = await api.post("doctors/updatedoctorstaffdata", payload);
            results.push({
              doctor_name: doctor.doctor_name,
              ok: String(res.data?.status).toLowerCase() === "success",
            });
          } catch (err) {
            results.push({
              doctor_name: doctor.doctor_name,
              ok: false,
              message: err?.response?.data?.message || err?.response?.data?.error || err?.message || "Update failed",
            });
          }
        }

        const failed = results.filter((item) => !item.ok);
        if (failed.length === 0) {
          Swal.fire(
            "Updated",
            `Assigned ${results.length} doctor(s) to ${currentStaff?.staff_name || "the selected staff"}.`,
            "success"
          );
        } else {
          Swal.fire(
            "Partial success",
            `${results.length - failed.length} doctor(s) updated, ${failed.length} failed. ${failed
              .map((item) => item.doctor_name)
              .join(", ")}`,
            "warning"
          );
        }

        setSelectedIds(new Set());
        await loadStaffDoctors(selectedStaff);
      } else {
        const res = await api.post("doctors/updatedoctortype", {
          doctor_ids: Array.from(selectedIds),
          doctor_type: Number(targetType),
          doctor_updby: userId,
        });

        if (String(res.data?.status).toLowerCase() === "success") {
          Swal.fire("Updated", res.data.message || "Doctor types updated successfully.", "success");
          setSelectedIds(new Set());
          await loadDoctors();
        } else {
          Swal.fire("Error", res.data?.message || "Could not update doctor types.", "error");
        }
      }
    } catch (err) {
      console.error("bulk assign error:", err);
      Swal.fire("Error", err?.response?.data?.message || err?.response?.data?.error || "Failed to update doctors.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-primary text-white d-flex flex-wrap gap-2 justify-content-between align-items-center py-3">
          <div>
            <h5 className="mb-0">Bulk Assign</h5>
            <div className="small opacity-75">Switch between doctor bulk assign and a staff doctor assign tab.</div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Button variant="light" size="sm" onClick={refreshCurrentView} disabled={loading || saving || staffLoading}>
              <Icon.ArrowClockwise size={14} className="me-1" />
              Refresh
            </Button>
            <Button variant="outline-light" size="sm" onClick={clearSelection} disabled={selectedIds.size === 0 || saving}>
              Clear Selection
            </Button>
          </div>
        </div>

        <div className="card-body">
          <div className="d-flex flex-wrap mb-3 border border-primary rounded" style={{ background: "#f8fbff", gap: "2px", padding: "2px" }}>
            <Button
              variant={viewMode === "doctor" ? "primary" : "light"}
              className="rounded flex-fill"
              style={{ minWidth: "180px" }}
              onClick={() => setViewMode("doctor")}
            >
              Doctor Bulk Assign
            </Button>
            <Button
              variant={viewMode === "staff" ? "primary" : "light"}
              className="rounded flex-fill"
              style={{ minWidth: "180px" }}
              onClick={() => setViewMode("staff")}
            >
              Staff Doctor Assign
            </Button>
          </div>

          {viewMode === "staff" ? (
            <>
              <div className="p-4 rounded border" style={{ background: "#f8fbff", borderColor: "#dbe7ff" }}>
                <div className="row g-3 align-items-end">
                  <div className="col-12 col-lg-4">
                    <Form.Label className="fw-semibold">Select staff</Form.Label>
                    <Form.Select value={selectedStaff} onChange={(e) => setSelectedStaff(e.target.value)}>
                      <option value="">Choose staff</option>
                      {staffOptions.map((staff) => (
                        <option key={staff.staff_id} value={String(staff.staff_id)}>
                          {staff.staff_name}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="col-12 col-lg-4">
                    <Form.Check
                      type="checkbox"
                      id="updateIntroBy"
                      label="Also update intro by to this staff"
                      checked={updateIntroBy}
                      onChange={(e) => setUpdateIntroBy(e.target.checked)}
                    />
                  </div>
                  <div className="col-12 col-lg-4 text-muted small">
                    <div className="fw-semibold text-dark">Selected staff</div>
                    <div>{currentStaff?.staff_name || "No staff selected"}</div>
                  </div>
                  <div className="col-12">
                    <div
                      className="alert alert-warning mb-0 small"
                      role="alert"
                      style={{ borderColor: "#f0c36d", background: "#fff8e6", color: "#8a5a00" }}
                    >
                      This option can be used to assign doctors to a staff member. For switching staffs for doctors, use the
                      Edit option in the Doctor Details option from the menu.
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-3 align-items-end my-3">
                <div className="col-12 col-lg-5">
                  <InputGroup>
                    <InputGroup.Text>
                      <Icon.Search size={14} />
                    </InputGroup.Text>
                    <Form.Control placeholder="Search staff doctors" value={search} onChange={(e) => setSearch(e.target.value)} />
                  </InputGroup>
                </div>
                <div className="col-12 col-lg-7">
                  <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                    <Button variant="outline-primary" size="sm" onClick={selectAllVisible} disabled={visibleStaffDoctors.length === 0 || saving || staffLoading}>
                      Select All Visible
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleSubmit} disabled={selectedIds.size === 0 || saving || staffLoading || !selectedStaff}>
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" />
                          Updating...
                        </>
                      ) : (
                        <>Assign {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mb-3 p-3 rounded" style={{ background: "#f8fbff", border: "1px solid #dbe7ff" }}>
                <div className="d-flex flex-wrap gap-3 small">
                  <div>
                    <strong>Total:</strong> {staffDoctors.length}
                  </div>
                  <div>
                    <strong>Visible:</strong> {visibleStaffDoctors.length}
                  </div>
                  <div>
                    <strong>Selected:</strong> {selectedIds.size}
                  </div>
                </div>
              </div>

              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div className="small text-muted">
                  Showing {visibleStaffDoctors.length === 0 ? 0 : (currentStaffPage - 1) * PAGE_SIZE + 1}
                  -{Math.min(currentStaffPage * PAGE_SIZE, visibleStaffDoctors.length)} of {visibleStaffDoctors.length}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => goToStaffPage(currentStaffPage - 1)}
                    disabled={currentStaffPage <= 1}
                  >
                    Prev
                  </Button>
                  <div className="small text-muted">
                    Page {currentStaffPage} of {staffTotalPages}
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => goToStaffPage(currentStaffPage + 1)}
                    disabled={currentStaffPage >= staffTotalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>

              {staffLoading ? (
                <div className="py-5 text-center text-muted">Loading doctors for this staff...</div>
              ) : visibleStaffDoctors.length === 0 ? (
                <div className="py-5 text-center text-muted">
                  {selectedStaff ? "No doctors available for this staff." : "Choose a staff member to view doctors."}
                </div>
              ) : (
                <div className="row g-3">
                  {pagedStaffDoctors.map((doctor) => {
                    const isSelected = selectedIds.has(doctor.doctor_id);
                    const typeMeta = getIndicatorMeta(doctor.doctor_type, "Core", "Secondary");
                    const introMeta = getIndicatorMeta(doctor.doctor_introby, "General", "Specific");

                    return (
                      <div key={doctor.doctor_id} className="col-12 col-sm-6 col-lg-4 col-xxl-3">
                        <label
                          className="card h-100 border-0 shadow-sm"
                          style={{
                            cursor: "pointer",
                            background: isSelected ? "#eaf5ef" : "#fff",
                            border: isSelected ? "1px solid #7bb58f" : "1px solid #eef2f6",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <div className="card-body p-3 d-flex gap-3 align-items-start">
                            <Form.Check
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelected(doctor.doctor_id)}
                              className="mt-1"
                            />
                            <div className="flex-grow-1 min-w-0">
                              <div className="fw-semibold text-truncate mb-2" style={{ fontSize: "14px" }}>
                                {doctor.doctor_name}
                              </div>
                              <div className="d-flex flex-wrap gap-2">
                                <span className="d-inline-flex align-items-center gap-1 small px-2 py-1 rounded-pill bg-light border">
                                  <span className={`rounded-circle ${typeMeta.dotClass}`} style={{ width: 8, height: 8 }} />
                                  <span>{typeMeta.label}</span>
                                </span>
                                <span
                                  className="d-inline-flex align-items-center justify-content-center small px-2 py-1 rounded-pill bg-light border"
                                  title={Number(doctor.doctor_introby) === 0 ? "Intro by: 0" : "Intro by: non-zero"}
                                >
                                  <span className={`rounded-circle ${introMeta.dotClass}`} style={{ width: 8, height: 8 }} />
                                </span>
                              </div>
                            </div>
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="row g-3 align-items-end mb-3">
                <div className="col-12 col-lg-4">
                  <Form.Label className="fw-semibold">Select target type</Form.Label>
                  <Form.Select value={targetType} onChange={(e) => setTargetType(e.target.value)}>
                    {TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-12 col-lg-5 text-muted small">
                  <div className="fw-semibold text-dark">{currentOption.label}</div>
                  <div>{currentOption.note}</div>
                </div>
                <div className="col-12 col-lg-3">
                  <InputGroup>
                    <InputGroup.Text>
                      <Icon.Search size={14} />
                    </InputGroup.Text>
                    <Form.Control placeholder="Search doctors" value={search} onChange={(e) => setSearch(e.target.value)} />
                  </InputGroup>
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2 mb-3">
                <Button variant="outline-primary" size="sm" onClick={selectAllVisible} disabled={eligibleDoctors.length === 0 || saving}>
                  Select All Visible
                </Button>
                <Button variant="primary" size="sm" onClick={handleSubmit} disabled={selectedIds.size === 0 || saving}>
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Updating...
                    </>
                  ) : (
                    <>Update {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}</>
                  )}
                </Button>
              </div>

              <div className="mb-3 p-3 rounded" style={{ background: "#f8fbff", border: "1px solid #dbe7ff" }}>
                <div className="d-flex flex-wrap gap-3 small">
                  <div>
                    <strong>Total:</strong> {doctors.length}
                  </div>
                  <div>
                    <strong>Eligible:</strong> {eligibleDoctors.length}
                  </div>
                  <div>
                    <strong>Selected:</strong> {selectedIds.size}
                  </div>
                </div>
              </div>

              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div className="small text-muted">
                  Showing {eligibleDoctors.length === 0 ? 0 : (currentDoctorPage - 1) * PAGE_SIZE + 1}
                  -{Math.min(currentDoctorPage * PAGE_SIZE, eligibleDoctors.length)} of {eligibleDoctors.length}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => goToDoctorPage(currentDoctorPage - 1)}
                    disabled={currentDoctorPage <= 1}
                  >
                    Prev
                  </Button>
                  <div className="small text-muted">
                    Page {currentDoctorPage} of {doctorTotalPages}
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => goToDoctorPage(currentDoctorPage + 1)}
                    disabled={currentDoctorPage >= doctorTotalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>

              {loading ? (
                <div className="py-5 text-center text-muted">Loading doctors...</div>
              ) : eligibleDoctors.length === 0 ? (
                <div className="py-5 text-center text-muted">
                  No doctors available for this type. Try switching the target type or clearing the search.
                </div>
              ) : (
                <div className="row g-3">
                  {pagedDoctors.map((doctor) => {
                    const currentType =
                      doctor.doctor_type === null || doctor.doctor_type === undefined || doctor.doctor_type === ""
                        ? null
                        : Number(doctor.doctor_type);
                    const isSelected = selectedIds.has(doctor.doctor_id);
                    const typeMeta = getIndicatorMeta(currentType, "Core", "Secondary");
                    const introMeta = getIndicatorMeta(doctor.doctor_introby, "General", "Specific");
                    return (
                      <div key={doctor.doctor_id} className="col-12 col-sm-6 col-lg-4 col-xxl-3">
                        <label
                          className="card h-100 border-0 shadow-sm"
                          style={{
                            cursor: "pointer",
                            background: isSelected ? "#eaf5ef" : "#fff",
                            border: isSelected ? "1px solid #7bb58f" : "1px solid #eef2f6",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <div className="card-body p-3 d-flex gap-3 align-items-start">
                            <Form.Check
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelected(doctor.doctor_id)}
                              className="mt-1"
                            />
                            <div className="flex-grow-1 min-w-0">
                              <div className="fw-semibold text-truncate mb-2" style={{ fontSize: "14px" }}>
                                {doctor.doctor_name}
                              </div>
                              <div className="d-flex flex-wrap gap-2">
                                <span className="d-inline-flex align-items-center gap-1 small px-2 py-1 rounded-pill bg-light border">
                                  <span className={`rounded-circle ${typeMeta.dotClass}`} style={{ width: 8, height: 8 }} />
                                  <span>{typeMeta.label}</span>
                                </span>
                                <span
                                  className="d-inline-flex align-items-center justify-content-center small px-2 py-1 rounded-pill bg-light border"
                                  title={Number(doctor.doctor_introby) === 0 ? "Intro by: 0" : "Intro by: non-zero"}
                                >
                                  <span className={`rounded-circle ${introMeta.dotClass}`} style={{ width: 8, height: 8 }} />
                                </span>
                              </div>
                            </div>
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DoctorBulkAssign;
